import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { LeafLogo } from "@/components/common/LeafLogo";
import { Button } from "@/components/ui/button";
import { Toaster } from "sonner";
import { OverviewSection } from "@/pages/admin/sections/OverviewSection";
import { CommitteesSection } from "@/pages/admin/sections/CommitteesSection";
import { MembersSection } from "@/pages/admin/sections/MembersSection";
import { DistrictsSection } from "@/pages/admin/sections/DistrictsSection";
import { GrsSection } from "@/pages/admin/sections/GrsSection";
import { TeamsSection } from "@/pages/admin/sections/TeamsSection";
import { NoticeSection } from "@/pages/admin/sections/NoticeSection";
import { AdsSection } from "@/pages/admin/sections/AdsSection";
import { SlideshowSection } from "@/pages/admin/sections/SlideshowSection";
import { SocialSection } from "@/pages/admin/sections/SocialSection";
import { IssuesSection } from "@/pages/admin/sections/IssuesSection";
import { CollectionsSection } from "@/pages/admin/sections/CollectionsSection";
import { DuesSection } from "@/pages/admin/sections/DuesSection";
import { ServiceSection } from "@/pages/admin/sections/ServiceSection";
import { AccessRequestsSection, AuditSection } from "@/pages/admin/sections/AccessAuditSections";
import { PagesSection, LabelsSection, MediaSection } from "@/pages/admin/sections/ContentSections";
import { SiteInfoSection, FeaturesSection, NavigationSection, HomeManagerSection, TemplatesSection, AdminUsersSection } from "@/pages/admin/sections/SiteConfigSections";
import { LoginSecuritySection } from "@/pages/admin/sections/LoginSecuritySection";
import { LettersSection } from "@/pages/admin/sections/LettersSection";
import { LayoutDashboard, Users, UserCog, MapPin, FileText, Trophy, Megaphone, AlertTriangle, Inbox, UserPlus, ScrollText, Wallet, LogOut, ExternalLink, Menu, X, BookOpen, Upload, User, CalendarClock, Images, BellRing, Share2, Settings, Navigation, Home as HomeIcon, Type, Mail, ToggleLeft, ShieldCheck, FileEdit, Mails } from "lucide-react";

const nav = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, C: OverviewSection },
  { key: "members", label: "Member Roster", icon: UserCog, C: MembersSection },
  { key: "service", label: "Service Details", icon: CalendarClock, C: ServiceSection },
  { key: "collections", label: "Collections", icon: Wallet, C: CollectionsSection },
  { key: "dues", label: "Arrears & Renewals", icon: BellRing, C: DuesSection },
  { key: "access", label: "Access Requests", icon: UserPlus, C: AccessRequestsSection },
  { key: "pages", label: "Pages", icon: FileEdit, C: PagesSection, editorOk: true },
  { key: "labels", label: "Text & Translations", icon: Type, C: LabelsSection, editorOk: true },
  { key: "media", label: "Media Library", icon: Images, C: MediaSection, editorOk: true },
  { key: "siteinfo", label: "Site Settings", icon: Settings, C: SiteInfoSection },
  { key: "navigation", label: "Navigation", icon: Navigation, C: NavigationSection },
  { key: "home", label: "Home Page", icon: HomeIcon, C: HomeManagerSection },
  { key: "templates", label: "Templates", icon: Mail, C: TemplatesSection },
  { key: "features", label: "Feature Switches", icon: ToggleLeft, C: FeaturesSection },
  { key: "adminusers", label: "Admin Users", icon: ShieldCheck, C: AdminUsersSection },
  { key: "loginsecurity", label: "Login Problems", icon: ShieldCheck, C: LoginSecuritySection },
  { key: "committees", label: "Committees", icon: Users, C: CommitteesSection },
  { key: "letters", label: "Letters", icon: Mails, C: LettersSection },
  { key: "districts", label: "Districts", icon: MapPin, C: DistrictsSection },
  { key: "grs", label: "GRs & Notices", icon: FileText, C: GrsSection },
  { key: "teams", label: "GAPL Teams", icon: Trophy, C: TeamsSection },
  { key: "notice", label: "Important Bar", icon: AlertTriangle, C: NoticeSection },
  { key: "ads", label: "Ad Slots", icon: Megaphone, C: AdsSection },
  { key: "slideshow", label: "Home Slideshow", icon: Images, C: SlideshowSection },
  { key: "social", label: "Social links", icon: Share2, C: SocialSection },
  { key: "issues", label: "Submissions", icon: Inbox, C: IssuesSection },
  { key: "audit", label: "Audit Log", icon: ScrollText, C: AuditSection },
];

const AdminDashboard = () => {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const isEditor = user?.role === "content_editor";
  const visibleNav = isEditor ? nav.filter((n) => n.editorOk) : nav;
  const [active, setActive] = useState(isEditor ? "pages" : "overview");
  const [mobileNav, setMobileNav] = useState(false);
  const [photo, setPhoto] = useState(user?.photo_url || "");
  const [uploading, setUploading] = useState(false);
  const Active = (visibleNav.find((n) => n.key === active) || visibleNav[0]).C;

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { toast.error("Only PNG or JPG images are allowed"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be 2 MB or smaller"); return; }
    setUploading(true);
    try { const res = await adminApi.uploadPhoto(file); setPhoto(res.photo_url); refresh && refresh(); toast.success("Photo updated"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const NavLinks = () => (
    <nav className="space-y-1">
      {visibleNav.map((n) => {
        const Icon = n.icon;
        return (
          <button
            key={n.key}
            data-testid={`admin-nav-${n.key}`}
            onClick={() => { setActive(n.key); setMobileNav(false); }}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              active === n.key ? "bg-gmoaa-green text-white" : "text-gmoaa-text-dark hover:bg-gmoaa-green-light"
            }`}
          >
            <Icon size={17} /> {n.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-gmoaa-surface">
      <Toaster position="top-center" richColors />

      {/* Sidebar (desktop) */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-gmoaa-border-subtle bg-gmoaa-cream p-4 lg:flex">
        <div className="mb-6 flex items-center gap-2">
          <LeafLogo size={40} />
          <div><p className="font-serif font-bold text-gmoaa-green">GMOAA</p><p className="text-[11px] text-gmoaa-text-muted">Admin Panel</p></div>
        </div>
        <NavLinks />
        <div className="mt-auto space-y-1 pt-4">
          <a href={adminApi.guideUrl()} target="_blank" rel="noopener noreferrer" data-testid="admin-guide-btn"
             className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <BookOpen size={17} /> User Guide (PDF)
          </a>
          <button onClick={() => window.open("/", "_blank")} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gmoaa-text-dark hover:bg-gmoaa-green-light">
            <ExternalLink size={17} /> View website
          </button>
          <button data-testid="admin-logout" onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gmoaa-red hover:bg-gmoaa-red-bg">
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1">
        <header className="flex items-center justify-between border-b border-gmoaa-border-subtle bg-gmoaa-cream px-4 py-3 lg:px-8">
          <button className="lg:hidden" onClick={() => setMobileNav((o) => !o)}>{mobileNav ? <X size={22} /> : <Menu size={22} />}</button>
          <p className="text-sm text-gmoaa-text-muted">Signed in as <span className="font-medium text-gmoaa-text-dark">{user?.email}</span></p>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 overflow-hidden rounded-full border border-gmoaa-border-subtle bg-gmoaa-surface">
              {photo ? <img src={mediaUrl(photo)} alt="" className="h-full w-full object-cover" data-testid="admin-avatar" /> : <div className="flex h-full w-full items-center justify-center text-gmoaa-text-muted"><User size={18} /></div>}
            </div>
            <label className="hidden cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-2.5 py-1.5 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light sm:inline-flex">
              <Upload size={13} /> {uploading ? "Uploading…" : "Photo"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onPhoto} disabled={uploading} data-testid="admin-photo-input" />
            </label>
            <Button size="sm" variant="ghost" className="text-gmoaa-red lg:hidden" onClick={doLogout}><LogOut size={16} /></Button>
          </div>
        </header>

        {mobileNav && (
          <div className="border-b border-gmoaa-border-subtle bg-gmoaa-cream p-4 lg:hidden"><NavLinks /></div>
        )}

        <main className="p-4 lg:p-8">
          <Active />
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
